import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Label } from "@/components/ui/label";
import { Phone, Mail, MapPin, Clock, Download } from "lucide-react";
import { useState } from "react";
import { useToast } from "@/hooks/use-toast";
export const ContactSection = () => {
  const {
    toast
  } = useToast();
  const [formData, setFormData] = useState({
    nom: "",
    entreprise: "",
    email: "",
    telephone: "",
    typeClient: "",
    produitInteresse: "",
    quantite: "",
    message: ""
  });
  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    toast({
      title: "Demande envoyée !",
      description: "Nous vous recontacterons dans les plus brefs délais."
    });
    // Reset form
    setFormData({
      nom: "",
      entreprise: "",
      email: "",
      telephone: "",
      typeClient: "",
      produitInteresse: "",
      quantite: "",
      message: ""
    });
  };
  const handleDownload = (docType: string) => {
    toast({
      title: "Téléchargement démarré",
      description: `Documentation ${docType} en cours de téléchargement...`
    });
  };
  return <section id="contactez-nous" className="py-20 bg-gray-50 scroll-mt-20">
      <div className="container mx-auto px-4">
        <div className="text-center mb-16">
          <h2 className="text-4xl font-bold text-gray-900 mb-4">
            Contactez-nous
          </h2>
          <p className="text-xl text-gray-600 max-w-3xl mx-auto">
            Obtenez votre devis personnalisé ou téléchargez notre documentation technique
          </p>
        </div>

        <div className="grid lg:grid-cols-3 gap-8">
          {/* Formulaire de devis */}
          <div className="lg:col-span-2">
            <Card>
              <CardHeader>
                <CardTitle className="text-2xl text-blue-600">Demande de Devis</CardTitle>
                <CardDescription>
                  Remplissez ce formulaire pour recevoir une offre personnalisée
                </CardDescription>
              </CardHeader>
              <CardContent>
                <form onSubmit={handleSubmit} className="space-y-6">
                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="nom">Nom complet *</Label>
                      <Input id="nom" value={formData.nom} onChange={e => setFormData({
                      ...formData,
                      nom: e.target.value
                    })} required />
                    </div>
                    <div>
                      <Label htmlFor="entreprise">Entreprise *</Label>
                      <Input id="entreprise" value={formData.entreprise} onChange={e => setFormData({
                      ...formData,
                      entreprise: e.target.value
                    })} required />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="email">Email *</Label>
                      <Input id="email" type="email" value={formData.email} onChange={e => setFormData({
                      ...formData,
                      email: e.target.value
                    })} required />
                    </div>
                    <div>
                      <Label htmlFor="telephone">Téléphone *</Label>
                      <Input id="telephone" type="tel" value={formData.telephone} onChange={e => setFormData({
                      ...formData,
                      telephone: e.target.value
                    })} required />
                    </div>
                  </div>

                  <div className="grid md:grid-cols-2 gap-4">
                    <div>
                      <Label htmlFor="typeClient">Type de client</Label>
                      <Select value={formData.typeClient} onValueChange={value => setFormData({
                      ...formData,
                      typeClient: value
                    })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Sélectionnez votre profil" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="carreleur">Carreleur</SelectItem>
                          <SelectItem value="macon">Maçon</SelectItem>
                          <SelectItem value="artisan">Artisan général</SelectItem>
                          <SelectItem value="entreprise">Entreprise BTP</SelectItem>
                          <SelectItem value="distributeur">Distributeur</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div>
                      <Label htmlFor="produitInteresse">Produit d'intérêt</Label>
                      <Select value={formData.produitInteresse} onValueChange={value => setFormData({
                      ...formData,
                      produitInteresse: value
                    })}>
                        <SelectTrigger>
                          <SelectValue placeholder="Quel produit vous intéresse ?" />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="elite-precision">Elite Precision (Carrelage)</SelectItem>
                          <SelectItem value="elitepro">ElitePro (Maçonnerie)</SelectItem>
                          <SelectItem value="les-deux">Les deux gammes</SelectItem>
                          <SelectItem value="autre">Autre produit</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  <div>
                    <Label htmlFor="quantite">Quantité estimée</Label>
                    <Input id="quantite" placeholder="Ex: 10 disques par mois" value={formData.quantite} onChange={e => setFormData({
                    ...formData,
                    quantite: e.target.value
                  })} />
                  </div>

                  <div>
                    <Label htmlFor="message">Message / Besoins spécifiques</Label>
                    <Textarea id="message" placeholder="Décrivez vos besoins, projets en cours, ou questions..." value={formData.message} onChange={e => setFormData({
                    ...formData,
                    message: e.target.value
                  })} rows={4} />
                  </div>

                  <Button type="submit" className="w-full bg-orange-500 hover:bg-orange-600 text-white">
                    Envoyer ma demande de devis
                  </Button>
                </form>
              </CardContent>
            </Card>
          </div>

          {/* Informations de contact et téléchargements */}
          <div className="space-y-6">
            {/* Coordonnées */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Nos Coordonnées</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div className="flex items-center space-x-3">
                  <Phone className="h-5 w-5 text-blue-600" />
                  <div>
                    <div className="font-semibold">+33 6 61 01 84 89</div>
                    <div className="text-sm text-gray-600">Service commercial</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Mail className="h-5 w-5 text-blue-600" />
                  <div>
                    <div className="font-semibold">contact@outilmatpro.fr</div>
                    <div className="text-sm text-gray-600">Réponse sous 24h</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <MapPin className="h-5 w-5 text-blue-600" />
                  <div>
                    <div className="font-semibold">7 Rue du Général de Monsabert</div>
                    <div className="text-sm text-gray-600">34500 Béziers, France</div>
                  </div>
                </div>
                <div className="flex items-center space-x-3">
                  <Clock className="h-5 w-5 text-blue-600" />
                  <div>
                    <div className="font-semibold">Lun-Ven 8h-18h</div>
                    
                  </div>
                </div>
              </CardContent>
            </Card>

            {/* Téléchargements */}
            <Card>
              <CardHeader>
                <CardTitle className="text-xl">Documentation Technique</CardTitle>
                <CardDescription>
                  Téléchargez nos fiches produits et guides d'utilisation
                </CardDescription>
              </CardHeader>
              <CardContent className="space-y-3">
                <Button variant="outline" className="w-full justify-start" onClick={() => handleDownload("Elite Precision")}>
                  <Download className="h-4 w-4 mr-2" />
                  Catalogue Elite Precision
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => handleDownload("ElitePro")}>
                  <Download className="h-4 w-4 mr-2" />
                  Catalogue ElitePro
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => handleDownload("Guide d'utilisation")}>
                  <Download className="h-4 w-4 mr-2" />
                  Guide d'utilisation
                </Button>
                <Button variant="outline" className="w-full justify-start" onClick={() => handleDownload("Tarifs professionnels")}>
                  <Download className="h-4 w-4 mr-2" />
                  Tarifs professionnels
                </Button>
              </CardContent>
            </Card>

            {/* Avantages */}
            <Card className="bg-blue-50">
              <CardContent className="p-6">
                <h3 className="font-bold text-blue-600 mb-3">Pourquoi nous choisir ?</h3>
                <ul className="space-y-2 text-sm">
                  <li>✓ Livraison gratuite dès 150€</li>
                  <li>✓ Garantie professionnelle</li>
                  <li>✓ Support technique expert</li>
                  <li>✓ Tarifs dégressifs</li>
                  <li>✓ Stock permanent</li>
                </ul>
              </CardContent>
            </Card>
          </div>
        </div>
      </div>
    </section>;
};